/**
 * AWS WAF token acquisition.
 *
 * The `challenge.js` SDK is loaded site-wide from app/views/layouts/base.html.erb
 * (only when WAF_INTEGRATION_URL is configured). It stores its token in the
 * `aws-waf-token` cookie, which same-origin XHR sends automatically -- so the
 * existing axios instances need no per-request header wiring.
 *
 * What the SDK does NOT guarantee is that the token has been acquired *before*
 * a user submits. Token acquisition is async and starts on page load; a fast
 * submit can race it. Callers therefore await ensureWafToken() immediately
 * before hitting an account endpoint.
 *
 * This module deliberately FAILS OPEN. If the SDK is absent, blocked, slow, or
 * throws, ensureWafToken() resolves to null and the caller proceeds. Client-side
 * enforcement would be security theatre in any case -- a bot simply would not
 * run this code. Enforcement belongs at the WAF, which decides what to do with
 * a request that arrives without a valid token. The only job here is to make
 * sure genuine browsers DO carry one, so that a token-absent rule can be turned
 * on at the edge without breaking real signups.
 */

// Long enough to cover an ordinary challenge round-trip, short enough that a
// hung SDK cannot wedge the signup form.
const TOKEN_TIMEOUT_MS = 2000;

function integration() {
  if (typeof window === 'undefined') {
    return null;
  }

  const sdk = window.AwsWafIntegration;
  if (!sdk || typeof sdk.getToken !== 'function') {
    return null;
  }

  return sdk;
}

/**
 * True when the WAF SDK is loaded and usable. Useful for diagnostics; callers
 * generally do not need it, since ensureWafToken() already degrades quietly.
 *
 * @returns {boolean}
 */
export function wafSdkPresent() {
  return integration() !== null;
}

/**
 * Resolve once a WAF token is available, or resolve to null if one cannot be
 * obtained. Never rejects, so it is safe to chain in front of a request without
 * altering that request's own error handling.
 *
 * @returns {Promise<string|null>}
 */
export function ensureWafToken() {
  const sdk = integration();
  if (!sdk) {
    return Promise.resolve(null);
  }

  let timer = null;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(function () {
      resolve(null);
    }, TOKEN_TIMEOUT_MS);
  });

  let settled;
  try {
    settled = Promise.race([sdk.getToken(), timeout]);
  } catch (e) {
    // getToken() throwing synchronously should not be possible, but a broken
    // or partially-loaded SDK is exactly the case this module exists to absorb.
    clearTimeout(timer);
    return Promise.resolve(null);
  }

  // No .finally() here on purpose: this bundle targets browsers older than
  // ES2018 and the loaded polyfill set does not reliably cover it.
  return settled.then(
    function (token) {
      clearTimeout(timer);
      return token || null;
    },
    function () {
      clearTimeout(timer);
      return null;
    }
  );
}

export default ensureWafToken;
